import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import classnames from 'classnames';

import { GrFormClose } from 'react-icons/gr';

import { ModalButton, ModalCloseHandler, ModalContent, ModalMessage, ModalTitle } from 'interface/modal';

// Binding modal to the appElement
Modal.setAppElement('#root');

interface BaseModalProps {
  buttons?: ModalButton[];
  title: ModalTitle;
  message?: ModalMessage;
  htmlContent?: ModalContent;
  modalClose?: ModalCloseHandler;
  onClose: () => void;
  withIcon?: React.ReactNode;
  renderFooter?: boolean;
  onCancelClick: () => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  loading?: boolean;
}

const BaseModal: React.FC<BaseModalProps> = (props) => {
  const [isOverFlown, setOverFlown] = useState<boolean>(false);

  const modelRef = useRef<HTMLDivElement | null>(null);

  const checkOverflow = (el: { scrollHeight: number; clientHeight: number } | null) =>
    el && el.scrollHeight > el.clientHeight;

  useEffect(() => {
    setOverFlown(checkOverflow(modelRef.current));
  }, []);

  const { buttons, title, message, htmlContent, modalClose, onClose, withIcon, renderFooter, onCancelClick, onClick } =
    props;

  return (
    <React.Fragment>
      {withIcon && <div className="lf-modal__status-icon">{withIcon}</div>}
      <div className="lf-modal__content-wrapper" onClick={onClick}>
        <div className={`lf-modal__header ${isOverFlown ? 'lf-modal__header--border' : ''}`}>
          {modalClose && (
            <div className="lf-modal__close-icon">
              <GrFormClose
                size={20}
                onClick={() => {
                  if (typeof modalClose === 'function') {
                    modalClose();
                  }
                  onClose();
                }}
              />
            </div>
          )}

          {title.type !== undefined ? (
            <div
              className={classnames('lf-modal__title', {
                [`lf-modal__title--${title.type}`]: title.type,
              })}
            >
              {title.text}
            </div>
          ) : (
            <div className="lf-modal__title">{title.text}</div>
          )}
        </div>
        {htmlContent && htmlContent !== undefined && (
          <div className={`lf-modal__body ${isOverFlown ? 'pt-5x' : ''}`} ref={modelRef}>
            {htmlContent}
          </div>
        )}

        {message && message !== undefined && (
          <div className="lf-modal__body">
            <p className={classnames('lf-modal__info', { [message.type]: message.type })}>{message.text}</p>
          </div>
        )}

        {renderFooter && (
          <div className={`lf-modal__footer ${isOverFlown ? 'lf-modal__footer--border' : ''}`}>
            {buttons &&
              buttons.map((value, index) => {
                const btnTheme = value.type === undefined ? 'primary' : value.type;
                return (
                  <button
                    className={classnames('btn btn--large', `btn--${btnTheme}`, value.className)}
                    onClick={async () => {
                      if (value.onClick) {
                        await value.onClick();
                      }

                      if (value.close) onCancelClick();
                    }}
                    disabled={value.disabled}
                    key={index}
                  >
                    {value.text}
                  </button>
                );
              })}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

interface CustomModalProps {
  isOpen: boolean;
  loading?: boolean;
  onClose: () => void;
  title: ModalTitle;
  modalStatusIcon?: React.ReactNode;
  buttons?: ModalButton[];
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  message?: ModalMessage;
  htmlContent?: ModalContent;
  closeButton?: boolean;
  renderFooter?: boolean;
  className?: string;
}

const CustomModal: React.FC<CustomModalProps> = (props) => {
  const {
    isOpen,
    loading,
    onClose,
    title,
    modalStatusIcon,
    buttons,
    onClick,
    message,
    htmlContent,
    closeButton,
    renderFooter,
    className,
  } = props;

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        className={`lf-modal ${className}`}
        overlayClassName="lf-modal__overlay"
        bodyOpenClassName="overflow-hidden"
      >
        <BaseModal
          title={title}
          loading={loading}
          buttons={buttons}
          message={message}
          onClose={onClose}
          onCancelClick={onClose}
          htmlContent={htmlContent}
          renderFooter={renderFooter}
          onClick={onClick}
          modalClose={closeButton ? onClose : ''}
          withIcon={modalStatusIcon ? modalStatusIcon : ''}
        />
      </Modal>
    </React.Fragment>
  );
};

export default CustomModal;
